import { AccountAction, DocumentStatus, DocumentType, type AccountStatus, type DocumentRecord } from '@lombok/shared';

/** Spanish copy for each account status action: the button, the dialog title and what it does. */
export const ACCOUNT_ACTION_COPY: Record<
  AccountAction,
  { button: string; title: string; effect: string; confirm: string; tone: 'accent' | 'danger' | 'outline' }
> = {
  [AccountAction.APPROVE]: {
    button: 'Aprobar cuenta',
    title: 'Aprobar cuenta',
    effect: 'La cuenta pasará a Activa. Sus próximos pedidos ya no quedarán pendientes de aprobación por la cuenta.',
    confirm: 'Aprobar',
    tone: 'accent',
  },
  [AccountAction.REJECT]: {
    button: 'Rechazar',
    title: 'Rechazar cuenta',
    effect: 'La cuenta quedará Rechazada y no podrá crear pedidos. Podrás reabrirla más adelante para evaluarla de nuevo.',
    confirm: 'Rechazar',
    tone: 'danger',
  },
  [AccountAction.HOLD]: {
    button: 'Poner en espera',
    title: 'Poner la cuenta en espera',
    effect: 'Los pedidos nuevos de esta cuenta quedarán pendientes de aprobación hasta que la reactives.',
    confirm: 'Poner en espera',
    tone: 'outline',
  },
  [AccountAction.SUSPEND]: {
    button: 'Suspender',
    title: 'Suspender cuenta',
    effect: 'La cuenta no podrá crear ni enviar pedidos hasta que la reactives. Los pedidos que ya existen no cambian.',
    confirm: 'Suspender',
    tone: 'danger',
  },
  [AccountAction.REACTIVATE]: {
    button: 'Reactivar',
    title: 'Reactivar cuenta',
    effect: 'La cuenta volverá a estar Activa y podrá hacer pedidos con normalidad.',
    confirm: 'Reactivar',
    tone: 'accent',
  },
  [AccountAction.REOPEN]: {
    button: 'Reabrir',
    title: 'Reabrir cuenta',
    effect: 'La cuenta volverá a En revisión para evaluarla de nuevo.',
    confirm: 'Reabrir',
    tone: 'outline',
  },
};

const STATUS_TEXT: Record<AccountStatus, string> = {
  pending: 'en revisión',
  active: 'activa',
  on_hold: 'en espera',
  suspended: 'suspendida',
  rejected: 'rechazada',
  closed: 'cerrada',
};

/** The status as an adjective for a sentence ("la cuenta está suspendida"). */
export function accountStatusText(status: AccountStatus): string {
  return STATUS_TEXT[status];
}

/** Why the status was last changed, worded for the detail screen. */
export function statusReasonLabel(status: AccountStatus): string | undefined {
  switch (status) {
    case 'rejected':
      return 'Motivo del rechazo';
    case 'on_hold':
      return 'Motivo de la espera';
    case 'suspended':
      return 'Motivo de la suspensión';
    default:
      return undefined;
  }
}

export type RifState = 'approved' | 'pending' | 'rejected' | 'missing';

/** What an admin needs to know about the account's RIF before approving it (newest first or not). */
export function rifState(documents: Pick<DocumentRecord, 'type' | 'status'>[]): RifState {
  const rifs = documents.filter((d) => d.type === DocumentType.RIF);
  if (rifs.some((d) => d.status === DocumentStatus.APPROVED)) return 'approved';
  if (rifs.some((d) => d.status === DocumentStatus.PENDING)) return 'pending';
  if (rifs.length > 0) return 'rejected';
  return 'missing';
}

export const RIF_STATE_LABEL: Record<RifState, string> = {
  approved: 'RIF aprobado',
  pending: 'RIF subido, pendiente de revisión',
  rejected: 'RIF rechazado: falta subir uno nuevo',
  missing: 'Aún no se subió el RIF',
};
